import { type FormEvent, useEffect, useState } from 'react';
import { i18n } from '@wms/shared';
import { ApiError, api } from '../lib/api.js';
import { Dialog } from './Dialog.js';
import { NumberInput } from './NumberInput.js';
import { SearchSelect } from './SearchSelect.js';

const { t } = i18n;

interface PartnerOpt {
  id: string;
  code: string;
  name: string;
}

interface SkuOpt {
  id: string;
  code: string;
  type: 'PRODUCT' | 'COMPONENT';
  nameLt: string;
  photoUrl: string | null;
  availableQty: number;
  maxProducibleQty: number | null;
}

interface DraftLine {
  skuId: string;
  qty: number;
  toShelfQty: number;
}

export function UnpackPalletDialog({
  palletId,
  palletIds,
  palletCodes,
  lockedPartner,
  registeredLines,
  onClose,
  onUnpacked,
}: {
  palletId?: string;
  /** Multiple blank pallets that receive the same checked content list. */
  palletIds?: string[];
  palletCodes?: string[];
  /**
   * When the pallet is tied to a partner (owned by / destined for), the unpack
   * target is locked to that partner — no other partner is offered.
   */
  lockedPartner?: PartnerOpt | null;
  registeredLines?: { skuId: string; qty: number }[];
  onClose: () => void;
  onUnpacked: () => void;
}) {
  const [partners, setPartners] = useState<PartnerOpt[]>([]);
  const [partnerId, setPartnerId] = useState<string>(lockedPartner?.id ?? '');
  const [skus, setSkus] = useState<SkuOpt[]>([]);
  const [lines, setLines] = useState<DraftLine[]>(() =>
    (registeredLines ?? []).map((line) => ({ ...line, toShelfQty: 0 })),
  );
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const bulkIds = palletIds ?? [];

  useEffect(() => {
    // Locked to the pallet's partner — no picker, no need to list others.
    if (lockedPartner) {
      setPartners([lockedPartner]);
      setPartnerId(lockedPartner.id);
      return;
    }
    // `/partners/options` (catalog.read) instead of `/partners` (users.manage)
    // so internal Operators can pick a partner without the admin capability.
    api<PartnerOpt[]>('/partners/options')
      .then((rows) => {
        setPartners(rows);
      })
      .catch(() => setPartners([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!partnerId) {
      setSkus([]);
      return;
    }
    // Both components and products can be unpacked into partner stock (the
    // stock ledger is keyed to any SKU). Jonas #6: was COMPONENT-only.
    api<SkuOpt[]>(`/partners/${partnerId}/skus`)
      .then((rows) => setSkus(rows))
      .catch(() => setSkus([]));
    // Reset lines when partner changes — SKUs may not exist anymore.
    setLines((registeredLines ?? []).map((line) => ({ ...line, toShelfQty: 0 })));
    // The dialog is remounted for each pallet; only a partner change should
    // reset confirmation lines while it is open.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [partnerId]);

  function addLine() {
    if (skus.length === 0) return;
    if (lines.some((line) => !line.skuId)) return;
    setLines((prev) => [...prev, { skuId: '', qty: 1, toShelfQty: 0 }]);
  }

  const linesValid =
    lines.length > 0 &&
    lines.every(
      (line) => line.skuId && line.qty > 0 && line.toShelfQty >= 0 && line.toShelfQty <= line.qty,
    );

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!linesValid || !partnerId || (!palletId && bulkIds.length < 2)) return;
    setError(null);
    setBusy(true);
    try {
      await api(palletId ? `/pallets/${palletId}/unpack` : '/pallets/bulk-unpack', {
        method: 'POST',
        body: {
          partnerId,
          lines: lines.map(({ skuId, qty }) => ({ skuId, qty })),
          shelfLines: lines
            .filter((line) => line.toShelfQty > 0)
            .map((line) => ({ skuId: line.skuId, qty: line.toShelfQty })),
          ...(!palletId ? { palletIds: bulkIds } : {}),
          ...(date ? { date } : {}),
        },
      });
      onUnpacked();
    } catch (err) {
      if (err instanceof ApiError && err.code === 'invalid_transition') {
        setError(t('pallet.unpack.invalid_state'));
      } else if (err instanceof ApiError && err.code === 'sku_not_in_partner') {
        setError(t('pallet.unpack.sku_partition'));
      } else if (err instanceof ApiError && err.code === 'pallet_contents_mismatch') {
        setError(t('pallet.unpack.contents_mismatch'));
      } else {
        setError(t('common.error'));
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      title={t(palletId ? 'pallet.unpack.title' : 'pallet.bulk_unpack.title')}
      onClose={onClose}
      size="wide"
      height="tall"
    >
      <form onSubmit={(e) => void onSubmit(e)} className="space-y-3 text-sm">
        {!palletId && palletCodes?.length ? (
          <div className="rounded border border-border bg-muted/40 p-2 text-xs">
            <div className="font-medium">
              {t('pallet.bulk_unpack.selected').replace('{{count}}', String(palletCodes.length))}
            </div>
            <div className="mt-1 font-mono text-muted-foreground">{palletCodes.join(' · ')}</div>
          </div>
        ) : null}
        <label className="block space-y-1">
          <span className="font-medium">{t('pallet.unpack.partner')}</span>
          {lockedPartner ? (
            <div className="w-full rounded border border-border bg-muted px-2 py-1.5">
              {lockedPartner.code} — {lockedPartner.name}
            </div>
          ) : (
            <select
              value={partnerId}
              onChange={(e) => setPartnerId(e.target.value)}
              required
              className="w-full rounded border border-border bg-background px-2 py-1.5"
            >
              <option value="">{t('common.select')}</option>
              {partners.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.code} — {p.name}
                </option>
              ))}
            </select>
          )}
        </label>

        <label className="block space-y-1">
          <span className="font-medium">{t('pallet.op_date.label')}</span>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="w-full rounded border border-border bg-background px-2 py-1.5"
          />
        </label>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="font-medium">{t('pallet.unpack.lines')}</span>
            <button
              type="button"
              onClick={() =>
                setLines((draft) => draft.map((line) => ({ ...line, toShelfQty: line.qty })))
              }
              disabled={lines.length === 0}
              className="rounded border border-border px-2 py-1 text-xs disabled:opacity-50"
            >
              {t('pallet.shelf.all')}
            </button>
            <button
              type="button"
              onClick={addLine}
              disabled={skus.length === 0 || lines.some((line) => !line.skuId)}
              className="rounded border border-border px-2 py-1 text-xs disabled:opacity-50"
            >
              {t('pallet.unpack.add_line')}
            </button>
          </div>
          {skus.length === 0 ? (
            <div className="rounded border border-dashed border-border p-3 text-center text-xs text-muted-foreground">
              {t('pallet.unpack.no_components')}
            </div>
          ) : null}
          {lines.map((line, i) => (
            <div key={i} className="space-y-2 rounded border border-border p-2">
              <div className="flex items-center gap-2">
                <SearchSelect
                  className="min-w-0 flex-1 text-sm"
                  menuSize="tall"
                  ariaLabel={t('bom.pick_component')}
                  value={line.skuId}
                  onChange={(id) =>
                    setLines((d) => d.map((l, j) => (j === i ? { ...l, skuId: id } : l)))
                  }
                  options={skus.map((s) => ({
                    id: s.id,
                    label: `${s.code} — ${s.nameLt} (${t(`sku.type.${s.type}`)})`,
                    imageUrl: s.photoUrl,
                    availableQty: s.availableQty,
                    maxProducibleQty: s.maxProducibleQty ?? undefined,
                  }))}
                />
                <NumberInput
                  integer
                  min={1}
                  value={line.qty}
                  onChange={(v) =>
                    setLines((d) => d.map((l, j) => (j === i ? { ...l, qty: v ?? 0 } : l)))
                  }
                  className="w-24 rounded border border-border bg-background px-2 py-1 text-sm tabular-nums"
                />
                <button
                  type="button"
                  onClick={() => setLines((d) => d.filter((_, j) => j !== i))}
                  className="rounded border border-border px-2 py-1 text-xs"
                >
                  ×
                </button>
              </div>
              <label className="flex items-center gap-2 text-xs">
                <input
                  type="checkbox"
                  checked={line.toShelfQty > 0}
                  onChange={(event) =>
                    setLines((draft) =>
                      draft.map((item, index) =>
                        index === i
                          ? { ...item, toShelfQty: event.target.checked ? item.qty : 0 }
                          : item,
                      ),
                    )
                  }
                />
                {t('pallet.shelf.action')}
              </label>
              {line.toShelfQty > 0 ? (
                <NumberInput
                  integer
                  min={1}
                  max={line.qty}
                  value={line.toShelfQty}
                  onChange={(value) =>
                    setLines((draft) =>
                      draft.map((item, index) =>
                        index === i ? { ...item, toShelfQty: value ?? 0 } : item,
                      ),
                    )
                  }
                  aria-label={`${t('pallet.shelf.qty')}: ${line.skuId}`}
                  className="w-24 rounded border border-border bg-background px-2 py-1 text-sm tabular-nums"
                />
              ) : null}
            </div>
          ))}
        </div>

        {error ? <div className="text-red-600">{error}</div> : null}

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-border px-3 py-1.5"
          >
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            disabled={busy || !linesValid || !partnerId || (!palletId && bulkIds.length < 2)}
            className="rounded bg-primary text-primary-foreground px-3 py-1.5 disabled:opacity-50"
          >
            {busy
              ? t('common.loading')
              : t(palletId ? 'pallet.unpack.submit' : 'pallet.bulk_unpack.submit')}
          </button>
        </div>
      </form>
    </Dialog>
  );
}
